/** @jsxImportSource ./simboluri-jsx */
// PAGINA TIPULUI DE VERIFICARE (mock-ui/design/StrVerificare.dc.html):
// obiectul „verificare-tip" — explicația din catalog (catalog_verificari.py,
// prin API) + TOATE elementele care o pică, ordonate după deficit, + soluțiile
// de la ieftin la scump. Catalogul e deja obiect pe server; aici primește URL.

import { useMemo } from "react";
import { useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { CategorieVerificare, StepState } from "./tipuri";
import { useCatalogVerificari } from "./useCatalog";
import { BaraRE } from "./ElementPage";
import { nr } from "./elemente";
import TextSimboluri from "./Simbol";
import { Ancora, hashElement, hashSectiune, linkAncora } from "./legaturi";

type Picata = {
  pas?: string;
  tip?: string;
  eticheta?: string;
  verificare?: string;
  element?: string;
  nivel?: number | string | null;
  RE?: number | null;
  descriere?: string;
  ancora?: Ancora | null;
};

const SURSA_ETICHETA: Record<string, Cheie> = {
  foaie: "struct.tipver.sursa_foaie",
  ro: "struct.tipver.sursa_ro",
  ec: "struct.tipver.sursa_ec",
};

export default function VerificareTipPage({
  id,
  tip,
  solutie,
  steps,
}: {
  id: string;
  tip: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
}) {
  const t = useT();
  const tn = useTn();
  const catalog = useCatalogVerificari();

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const r = steps["optimizare_structura"]?.result as any;
  const stare = r?.raport_solutie?.final ?? r?.stare_finala ?? null;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const picate: Picata[] = useMemo(
    () => ((stare?.picate ?? []) as Picata[]).filter((p) => p.tip === tip),
    [stare, tip]
  );

  const cat: CategorieVerificare | undefined = catalog?.categorii?.[tip];
  const titlu = cat?.eticheta ?? picate[0]?.eticheta ?? tip;

  // tiparul comun al picatelor — dacă toate sunt pe același nivel, spune-o:
  // adesea o singură decizie le rezolvă pe toate
  const niveluri = [...new Set(picate.map((p) => String(p.nivel ?? "")))].filter(Boolean);

  return (
    <div className="ob-pagina" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="ob-pas-antet" style={{ paddingTop: 0 }}>
        <a href={hashSectiune(id, "verificari", solutie ?? null)}>{t("struct.tipver.verificari")}</a>
        <span>/</span>
        <b style={{ color: "var(--ink)" }}>{titlu}</b>
        <span className="sg-mono" style={{ fontSize: 10 }}>{tip}</span>
      </div>

      {/* explicația din catalog */}
      {cat && (
        <div className="ob-card" style={{ padding: "14px 18px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <b style={{ fontSize: 14 }}>{cat.eticheta}</b>
            <span className="ob-chip ob-c-mut">{cat.pas_nume}</span>
            {cat.articol && <span className="ob-chip ob-c-mut">{cat.articol}</span>}
            {cat.sursa_formula && (
              <span className="ob-chip ob-c-acc" title={t("struct.tipver.prioritate")}>
                {SURSA_ETICHETA[cat.sursa_formula]
                  ? t(SURSA_ETICHETA[cat.sursa_formula])
                  : cat.sursa_formula_nume ?? cat.sursa_formula}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--mut)" }}>{cat.ce}</div>
          {cat.formula && (
            <div className="sg-mono" style={{ fontSize: 12, background: "var(--app)", borderRadius: 7, padding: "8px 10px" }}>
              <TextSimboluri text={cat.formula} locale={cat.simboluri} />
            </div>
          )}
          {cat.divergenta && (
            <div style={{ fontSize: 12 }}>
              <span className="ob-chip ob-c-met">{t("struct.tipver.divergenta")}</span>{" "}
              <span style={{ color: "var(--mut)" }}>
                {t("struct.tipver.divergenta_text", {
                  implementat: cat.divergenta.implementat,
                  normativ: cat.divergenta.normativ,
                  efect: cat.divergenta.efect,
                })}
              </span>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {/* elementele care o pică */}
        <div className="ob-card" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <b>{t("struct.tipver.care_pica")}</b>
            <span className={`ob-chip ${picate.length ? "ob-c-bad" : "ob-c-ok"}`}>
              {picate.length || t("struct.tipver.niciunul")}
            </span>
            <span className="ob-acasa-sub">{t("struct.tipver.dupa_deficit")}</span>
          </div>
          {picate.length > 0 && (
            <table className="ob-sol-tabel">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>{t("verificari.col_element")}</th>
                  <th style={{ width: 80 }}>{t("struct.top.nivel")}</th>
                  <th style={{ width: 150 }}>{t("struct.tipver.col_re")}</th>
                  <th style={{ width: 70, textAlign: "right" }}>{t("struct.tipver.col_deficit")}</th>
                  <th>{t("struct.tipver.col_detaliu")}</th>
                  <th style={{ width: 130 }} />
                </tr>
              </thead>
              <tbody>
                {[...picate]
                  .sort((a, b) => (a.RE ?? 1) - (b.RE ?? 1))
                  .map((p, i) => {
                    const link = linkAncora(id, solutie, p.ancora);
                    const ePlan = /^(GR|ST|PB|OP)\d/.test(String(p.element ?? ""));
                    return (
                      <tr key={i}>
                        <td>
                          {ePlan ? (
                            <a className="ob-ref" href={hashElement(id, String(p.element), solutie)}>
                              {p.element}
                            </a>
                          ) : (
                            <span className="sg-mono" style={{ fontSize: 12 }}>{p.element ?? "—"}</span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: "var(--mut)" }}>{p.nivel ?? "—"}</td>
                        <td><BaraRE re={p.RE ?? null} latime={76} /></td>
                        <td className="sg-mono" style={{ textAlign: "right", color: "var(--bad)", fontSize: 12 }}>
                          {p.RE != null ? `−${nr(Math.max(0, (1 - p.RE) * 100))} %` : "—"}
                        </td>
                        <td style={{ fontSize: 12, color: "var(--mut)" }}>
                          {p.descriere ?? p.verificare ?? ""}
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {link && (
                            <a href={link.href} title={link.titlu} style={{ fontSize: 11 }}>
                              {t("struct.tipver.link", { eticheta: link.eticheta })}
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
          {picate.length > 1 && niveluri.length === 1 && (
            <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)", display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
              <span className="ob-chip ob-c-mut">
                {tn("struct.tipver.toate_la#n", picate.length, { nivel: niveluri[0] })}
              </span>
              <span style={{ color: "var(--mut)" }}>
                {t("struct.tipver.tipar")}
              </span>
            </div>
          )}
        </div>

        {/* soluțiile din catalog, de la ieftin la scump */}
        {cat && (cat.solutii?.length ?? 0) > 0 && (
          <div style={{ width: 300, flex: "none", display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="ob-card" style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 8 }}>
              <span className="ob-lbl">{t("struct.tipver.solutii")}</span>
              {cat.solutii.map((s, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "7px 0", borderTop: "1px solid var(--line)" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <b style={{ fontSize: 12 }}>{i + 1} · {s.ce}</b>
                    <span style={{ flex: 1 }} />
                    <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}>{s.cost}</span>
                  </div>
                  <span style={{ color: "var(--mut)", fontSize: 11 }}>{s.efect}</span>
                  {s.automat && (
                    <span className="ob-chip ob-c-acc" style={{ fontSize: 10, alignSelf: "flex-start" }}
                          title={s.automat}>
                      {t("struct.tipver.automat")}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {cat.cand && (
              <div className="ob-card" style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 6 }}>
                <span className="ob-lbl">{t("struct.tipver.cand")}</span>
                <div style={{ fontSize: 12, color: "var(--mut)" }}>{cat.cand}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
